import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import toastUtils from '../utils/toast-utils';
import {
  Newspaper, Users, CheckCircle, XCircle, AlertTriangle, LogOut,
  Search, Filter, Download, Upload, RefreshCw, Eye, Trash2, Edit,
  ChevronLeft, ChevronRight, FileSpreadsheet, Plus, X, ChevronDown, Check,
  ChevronsUpDown, ChevronUp, Star
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import CardPreviewModal from '../components/CardPreviewModal';
import ImportModal from '../components/ImportModal';
import CardFormModal from '../components/CardFormModal';
import ProfileModal from '../components/ProfileModal';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CustomDropdown = ({ options, value, onChange, placeholder, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => String(opt.value) === String(value));

  return (
    <div className="relative min-w-[120px] sm:min-w-[140px]" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-5 py-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-[#D0021B]/30 dark:border-gray-700 rounded-2xl hover:border-[#D0021B] dark:hover:border-[#FF4D4D] focus:ring-2 focus:ring-[#D0021B]/50 dark:focus:ring-[#FF4D4D]/50 focus:border-[#D0021B] dark:focus:border-[#FF4D4D] focus:outline-none transition-all duration-300 text-[#1A1A1A] dark:text-white font-semibold hover:bg-white/95 dark:hover:bg-gray-800/95 cursor-pointer"
        data-testid={id}
      >
        <span className="truncate pr-4">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-[#D0021B] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute z-50 w-full mt-2 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-white/40 dark:border-gray-700/40 rounded-2xl shadow-2xl overflow-hidden max-h-64 flex flex-col"
          >
            <style>{`
              .custom-scrollbar::-webkit-scrollbar {
                width: 6px;
              }
              .custom-scrollbar::-webkit-scrollbar-track {
                background: transparent;
                margin: 4px;
              }
              .custom-scrollbar::-webkit-scrollbar-thumb {
                background-color: #D0021B;
                border-radius: 20px;
                opacity: 0.8;
              }
              .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                background-color: #A50015;
              }
            `}</style>
            <div className="p-2 overflow-y-auto custom-scrollbar flex-1">
              <button
                onClick={() => {
                  onChange('');
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm rounded-xl transition-all duration-200 ${value === ''
                  ? 'bg-[#D0021B] text-white shadow-md font-bold'
                  : 'text-[#666666] dark:text-gray-300 hover:bg-[#D0021B]/10 dark:hover:bg-[#FF4D4D]/20 hover:text-[#D0021B] dark:hover:text-[#FF4D4D] font-medium'
                  }`}
              >
                <span>{placeholder}</span>
                {value === '' && <Check className="w-4 h-4" />}
              </button>
              {options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 mt-1 text-sm rounded-xl transition-all duration-200 ${String(value) === String(opt.value)
                    ? 'bg-[#D0021B] text-white shadow-md font-bold'
                    : 'text-[#666666] dark:text-gray-300 hover:bg-[#D0021B]/10 dark:hover:bg-[#FF4D4D]/20 hover:text-[#D0021B] dark:hover:text-[#FF4D4D] font-medium'
                    }`}
                >
                  <span className="truncate pr-4">{opt.label}</span>
                  {String(value) === String(opt.value) && <Check className="w-4 h-4 flex-shrink-0" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const EntriesDropdown = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find(o => String(o.value) === String(value));

  return (
    <div className="flex items-center gap-2.5" ref={ref}>
      {/* Label */}
      <span className="text-xs font-bold uppercase tracking-widest text-[#666666] dark:text-gray-400 select-none whitespace-nowrap">
        Show
      </span>

      {/* Trigger */}
      <div className="relative">
        <button
          onClick={() => setIsOpen(p => !p)}
          className={`
            group flex items-center gap-2 pl-4 pr-3 py-2
            bg-white dark:bg-gray-800/80
            border rounded-xl shadow-sm
            text-sm font-semibold text-[#1A1A1A] dark:text-white
            transition-all duration-200 cursor-pointer
            ${isOpen
              ? 'border-[#D0021B] ring-2 ring-[#D0021B]/20 shadow-[#D0021B]/10 shadow-md'
              : 'border-gray-200 dark:border-gray-700 hover:border-[#D0021B]/50 hover:shadow-md'
            }
          `}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {/* Entries count badge */}
          <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-lg bg-[#D0021B]/10 dark:bg-[#D0021B]/20 text-[#D0021B] dark:text-[#ff6b6b] font-black text-sm leading-none transition-colors">
            {selected?.value ?? value}
          </span>
          <span className="text-[#666666] dark:text-gray-300 font-medium text-xs pr-1">entries</span>
          <ChevronDown
            className={`w-3.5 h-3.5 text-[#D0021B] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Dropdown panel */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.97 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="absolute left-0 top-[calc(100%+6px)] z-50 min-w-[140px] bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700/60 rounded-2xl shadow-2xl shadow-black/10 dark:shadow-black/40 overflow-hidden"
              role="listbox"
            >
              {/* Header decoration */}
              <div className="h-0.5 w-full bg-gradient-to-r from-[#D0021B] via-[#ff6b6b] to-transparent" />

              <div className="p-1.5">
                {options.map((opt, idx) => {
                  const isActive = String(opt.value) === String(value);
                  return (
                    <button
                      key={opt.value}
                      role="option"
                      aria-selected={isActive}
                      onClick={() => { onChange(opt.value); setIsOpen(false); }}
                      className={`
                        w-full flex items-center justify-between gap-3
                        px-3.5 py-2.5 rounded-xl text-sm font-semibold
                        transition-all duration-150 cursor-pointer
                        ${idx > 0 ? 'mt-0.5' : ''}
                        ${isActive
                          ? 'bg-gradient-to-r from-[#D0021B] to-[#ff4444] text-white shadow-md shadow-[#D0021B]/20'
                          : 'text-[#444] dark:text-gray-300 hover:bg-[#D0021B]/8 dark:hover:bg-[#D0021B]/15 hover:text-[#D0021B] dark:hover:text-[#ff6b6b]'
                        }
                      `}
                    >
                      <div className="flex items-center gap-2.5">
                        {/* Mini number pill */}
                        <span className={`
                          inline-flex items-center justify-center w-7 h-6 rounded-md text-xs font-black leading-none
                          ${isActive
                            ? 'bg-white/20 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-[#D0021B] dark:text-[#ff6b6b]'
                          }
                        `}>
                          {opt.value}
                        </span>
                        <span className="leading-none">{isActive ? 'entries' : opt.label.replace(`${opt.value} `, '')}</span>
                      </div>
                      {isActive && (
                        <Check className="w-3.5 h-3.5 flex-shrink-0 opacity-90" />
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

    </div>
  );
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedCard, setSelectedCard] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllMatchingSearch, setSelectAllMatchingSearch] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== searchInput) {
        setSearch(searchInput);
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  // Handle column sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to asc
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Dark Mode toggling side effect
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Fetch dashboard stats
  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/dashboard/stats`);
      return data;
    },
  });

  // Fetch cards
  const { data: cardsData, isLoading } = useQuery({
    queryKey: ['cards', page, limit, search, statusFilter, designationFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (designationFilter) params.append('designation', designationFilter);

      const { data } = await axios.get(`${API_URL}/api/cards?${params}`);
      return data;
    },
  });

  // Fetch designations
  const { data: designations } = useQuery({
    queryKey: ['designations'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/designations`);
      return data;
    },
  });

  // Delete card mutation
  const deleteMutation = useMutation({
    mutationFn: async (cardId) => {
      await axios.delete(`${API_URL}/api/cards/${cardId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['cards']);
      queryClient.invalidateQueries(['dashboard-stats']);
      toastUtils.success('Card deleted successfully');
    },
    onError: (error) => {
      toastUtils.error(error.response?.data?.detail || 'Failed to delete card');
    },
  });

  const addDesignationMutation = useMutation({
    mutationFn: async (designation_name) => {
      const { data } = await axios.post(`${API_URL}/api/designations`, { designation_name });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['designations']);
      toast.success('Designation added successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to add designation');
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (payload) => {
      const { data } = await axios.post(`${API_URL}/api/cards/bulk-delete`, payload);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['cards']);
      queryClient.invalidateQueries(['dashboard-stats']);
      setSelectedIds([]);
      setSelectAllMatchingSearch(false);
      toastUtils.success(data.message || 'Records deleted successfully');
    },
    onError: (error) => {
      toastUtils.error(error.response?.data?.error || 'Failed to delete records');
    },
  });

  const toggleSelectId = (id) => {
    setSelectAllMatchingSearch(false);
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAllPage = () => {
    const currentPageIds = cards.map(c => c._id || c.card_id);
    const allSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.includes(id));

    if (allSelected || selectAllMatchingSearch) {
      setSelectAllMatchingSearch(false);
      setSelectedIds([]);
    } else {
      setSelectAllMatchingSearch(true);
      setSelectedIds(currentPageIds);
    }
  };

  const handleBulkDelete = async () => {
    const count = selectAllMatchingSearch ? (pagination.total || 'All') : selectedIds.length;
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Bulk Delete?',
      html: `Are you sure you want to delete <strong>${count}</strong> record(s)?<br/><br/>This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonColor: '#D0021B',
      cancelButtonColor: '#5C5C5C',
      confirmButtonText: 'Yes, Delete All',
      customClass: { title: 'font-serif' },
    });

    if (result.isConfirmed) {
      bulkDeleteMutation.mutate({
        ids: selectAllMatchingSearch ? [] : selectedIds,
        allMatching: selectAllMatchingSearch,
        filters: { search, status: statusFilter, designation: designationFilter }
      });
    }
  };

  const handleAddDesignation = async () => {
    const { value: designationName } = await Swal.fire({
      title: 'Add Designation',
      input: 'text',
      inputLabel: 'Designation Name',
      inputPlaceholder: 'Enter designation name',
      showCancelButton: true,
      confirmButtonColor: '#D0021B',
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return 'You need to write something!';
        }
      },
      customClass: {
        title: 'font-serif',
      },
    });

    if (designationName) {
      addDesignationMutation.mutate(designationName);
    }
  };

  const handleDelete = async (card) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Card?',
      html: `Are you sure you want to remove <strong>${card.full_name}</strong>?<br/><br/>This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonColor: '#D0021B',
      cancelButtonColor: '#5C5C5C',
      confirmButtonText: 'Yes, Delete',
      customClass: {
        title: 'font-serif',
      },
    });

    if (result.isConfirmed) {
      deleteMutation.mutate(card._id || card.card_id);
    }
  };



  const handleExport = async (status = '') => {
    try {
      const response = await axios.get(`${API_URL}/api/cards/export${status ? `?status=${status}` : ''}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `press_cards_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toastUtils.success('Export completed successfully');
    } catch (error) {
      toastUtils.error('Failed to export data');
    }
  };

  const handleExportClick = async () => {
    const result = await Swal.fire({
      title: 'Export Cards',
      html: 'Select which cards to export:',
      input: 'select',
      inputOptions: {
        '': 'All Cards',
        'active': 'Active Only',
        'expired': 'Expired Only',
        'suspended': 'Suspended Only',
      },
      showCancelButton: true,
      confirmButtonColor: '#D0021B',
      confirmButtonText: 'Export',
      customClass: {
        title: 'font-serif',
      },
    });

    if (result.isConfirmed) {
      handleExport(result.value);
    }
  };

  const getStatusBadge = (card) => {
    const status = card.card_status;
    const daysUntilExpiry = card.days_until_expiry;

    if (status === 'suspended') {
      return <span className="badge-suspended px-2 py-1 rounded-full text-xs font-medium">Suspended</span>;
    }
    if (status === 'expired' || daysUntilExpiry < 0) {
      return <span className="badge-expired px-2 py-1 rounded-full text-xs font-medium">Expired</span>;
    }
    if (status === 'pending') {
      return <span className="badge-pending px-2 py-1 rounded-full text-xs font-medium">Pending</span>;
    }
    if (daysUntilExpiry !== undefined && daysUntilExpiry <= 1 && daysUntilExpiry >= 0) {
      return <span className="badge-expiring px-2 py-1 rounded-full text-xs font-medium">Expiring Soon</span>;
    }
    return <span className="badge-active px-2 py-1 rounded-full text-xs font-medium">Active</span>;
  };

  const getExpiryColor = (daysUntilExpiry) => {
    if (daysUntilExpiry < 0) return 'text-red-600';
    if (daysUntilExpiry <= 30) return 'text-amber-600';
    return 'text-green-600';
  };

  const cards = useMemo(() => cardsData?.cards || [], [cardsData?.cards]);
  const pagination = cardsData?.pagination || { page: 1, total: 0, total_pages: 1 };

  // Client-side sorting for the current page only
  const sortedCards = useMemo(() => {
    if (!sortBy) return cards;

    return [...cards].sort((a, b) => {
      let valA = a[sortBy];
      let valB = b[sortBy];

      if (valA === null || valA === undefined) valA = '';
      if (valB === null || valB === undefined) valB = '';

      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      } else {
        return sortOrder === 'asc' ? valA - valB : valB - valA;
      }
    });
  }, [cards, sortBy, sortOrder]);

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'expired', label: 'Expired' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'pending', label: 'Pending' }
  ];

  const designationOptions = designations?.map(d => ({
    value: d.designation_id,
    label: d.designation_name
  })) || [];

  const limitOptions = [
    { value: 10, label: '10 Entries' },
    { value: 25, label: '25 Entries' },
    { value: 50, label: '50 Entries' },
    { value: 100, label: '100 Entries' }
  ];

  const renderSortableHeader = (label, field, className = "") => (
    <th
      onClick={() => field && handleSort(field)}
      className={`px-4 py-4 text-left font-bold text-[#666666] dark:text-gray-400 capitalize whitespace-nowrap ${field ? 'cursor-pointer hover:bg-gray-100/50 dark:hover:bg-gray-800/50' : ''} transition-colors ${className}`}
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        {field && (
          <span className="text-gray-400 dark:text-gray-500">
            {sortBy === field ? (
              sortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-[#1A1A1A] dark:text-gray-200" /> : <ChevronDown className="w-3.5 h-3.5 text-[#1A1A1A] dark:text-gray-200" />
            ) : (
              <ChevronsUpDown className="w-3.5 h-3.5 opacity-40 hover:opacity-100 transition-opacity" />
            )}
          </span>
        )}
      </div>
    </th>
  );

  return (
    <div className="min-h-screen bg-[#F5F7F9]">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-screen w-20 bg-gradient-to-b from-[#D0021B] to-[#9B0114] flex flex-col items-center py-8 z-50 shadow-2xl transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex-1 flex flex-col items-center gap-8">
          <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center hover:bg-white/20 transition-all cursor-pointer group">
            <Newspaper className="w-7 h-7 text-white group-hover:scale-110 transition-transform" />
          </div>
          <div className="flex flex-col items-center group cursor-default py-10">
            <div className="h-64 flex items-center justify-center">
              <span className="[writing-mode:vertical-lr] rotate-180 text-white font-black text-[16px] tracking-[0.6em] opacity-30 group-hover:opacity-100 group-hover:tracking-[0.8em] transition-all duration-700 uppercase whitespace-nowrap select-none">
                Fact India News
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={logout}
          className="w-12 h-12 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 rounded-xl transition-all group"
          title="Logout"
          data-testid="logout-button"
        >
          <LogOut className="w-6 h-6 group-hover:rotate-12 transition-transform" />
        </button>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-20 min-h-screen relative dark:bg-gray-950 transition-colors duration-300">
        {/* Global Dashboard Navbar */}
        <Navbar
          user={user}
          onLogout={logout}
          onProfileClick={() => setShowProfileModal(true)}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
        />

        {/* Page Content with top padding to account for fixed Navbar */}
        <div className="px-4 pb-4 sm:px-6 sm:pb-6 lg:px-8 lg:pb-8 pt-20 sm:pt-24">
          {/* Page Actions Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4">
            <div>
              <h1 className="font-serif text-xl sm:text-2xl text-[#1A1A1A] dark:text-gray-100 transition-colors">Press ID Dashboard</h1>
              <p className="text-sm text-[#666666] dark:text-gray-400 transition-colors">Welcome back, {user?.full_name || user?.email}</p>
            </div>

            <div className="relative group">
              <button
                onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#D0021B] text-white rounded-xl hover:bg-[#A50015] transition-all shadow-lg hover:shadow-[#D0021B]/20 active:scale-95 shimmer-btn font-medium"
                data-testid="actions-dropdown-button"
              >

                <span>Actions</span>
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-300 ${showActionsDropdown ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showActionsDropdown && (
                  <>
                    {/* Backdrop for mobile/click-away */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowActionsDropdown(false)}
                    />

                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 10, x: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0, x: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 10, x: 20 }}
                      className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-2xl z-50 overflow-hidden py-2"
                    >
                      <div className="px-4 py-2 border-b border-gray-50 dark:border-gray-800 mb-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">Add New</p>
                      </div>

                      <button
                        onClick={() => {
                          setShowCardForm(true);
                          setShowActionsDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#D0021B]/5 hover:text-[#D0021B] dark:hover:bg-red-900/10 dark:hover:text-red-400 transition-colors"
                      >
                        <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                          <Plus className="w-4 h-4" />
                        </div>
                        <span className="font-semibold">Add New Card</span>
                      </button>

                      <button
                        onClick={() => {
                          handleAddDesignation();
                          setShowActionsDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#D0021B]/5 hover:text-[#D0021B] dark:hover:bg-red-900/10 dark:hover:text-red-400 transition-colors"
                      >
                        <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                          <Plus className="w-4 h-4" />
                        </div>
                        <span className="font-semibold">Add Designation</span>
                      </button>

                      <div className="px-4 py-2 border-b border-gray-50 dark:border-gray-800 my-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">Data Management</p>
                      </div>

                      <button
                        onClick={() => {
                          setShowImportModal(true);
                          setShowActionsDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#D0021B]/5 hover:text-[#D0021B] dark:hover:bg-red-900/10 dark:hover:text-red-400 transition-colors"
                      >
                        <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <Upload className="w-4 h-4" />
                        </div>
                        <span className="font-semibold">Import Cards</span>
                      </button>

                      <button
                        onClick={() => {
                          handleExportClick();
                          setShowActionsDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#D0021B]/5 hover:text-[#D0021B] dark:hover:bg-red-900/10 dark:hover:text-red-400 transition-colors"
                      >
                        <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <Download className="w-4 h-4" />
                        </div>
                        <span className="font-semibold">Export Data</span>
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0 }}
              className="group bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 p-4 sm:p-6 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-default"
              data-testid="stat-total-cards"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-[#D0021B] dark:text-[#FF4D4D] group-hover:scale-105 transition-transform origin-left">Total Cards</p>
                  <p className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] dark:text-gray-100 mt-2">{stats?.total_cards || 0}</p>
                </div>
                <div className="p-2 sm:p-3 bg-red-100 dark:bg-red-900/30 rounded-xl group-hover:rotate-12 transition-transform duration-300">
                  <Users className="w-6 h-6 sm:w-8 sm:h-8 text-[#D0021B] dark:text-[#FF4D4D]" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="group bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/20 p-4 sm:p-6 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-default"
              data-testid="stat-active-cards"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-[#16A34A] dark:text-[#22c55e] group-hover:scale-105 transition-transform origin-left">Active</p>
                  <p className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] dark:text-gray-100 mt-2">{stats?.active_cards || 0}</p>
                </div>
                <div className="p-2 sm:p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl group-hover:rotate-12 transition-transform duration-300">
                  <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-[#16A34A] dark:text-[#22c55e]" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="group bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/20 p-4 sm:p-6 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-default"
              data-testid="stat-expired-cards"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-[#DC2626] dark:text-[#ef4444] group-hover:scale-105 transition-transform origin-left">Expired</p>
                  <p className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] dark:text-gray-100 mt-2">{stats?.expired_cards || 0}</p>
                </div>
                <div className="p-2 sm:p-3 bg-rose-100 dark:bg-rose-900/30 rounded-xl group-hover:rotate-12 transition-transform duration-300">
                  <XCircle className="w-6 h-6 sm:w-8 sm:h-8 text-[#DC2626] dark:text-[#ef4444]" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="group bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 p-4 sm:p-6 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-default"
              data-testid="stat-expiring-cards"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-[#D97706] dark:text-[#f59e0b] group-hover:scale-105 transition-transform origin-left">Expiring Soon</p>
                  <p className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] dark:text-gray-100 mt-2">{stats?.expiring_soon || 0}</p>
                </div>
                <div className="p-2 sm:p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl group-hover:rotate-12 transition-transform duration-300">
                  <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-[#D97706] dark:text-[#f59e0b]" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Table Container */}
          <div className="bg-white dark:bg-gray-900 border border-[#E5E5E5] dark:border-gray-800 rounded-xl shadow-sm overflow-hidden transition-all duration-300">
            {/* Table Header Bar */}
            <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-[#E5E5E5] dark:border-gray-800 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-3">
              {/* Left Side: Title */}
              <div className="flex items-center gap-4 sm:gap-6">
                <h2 className="text-lg sm:text-xl font-serif font-bold text-[#1A1A1A] dark:text-white">Press ID Cards</h2>
              </div>

              {/* Right Side: Entries, Search & Filter Actions */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full sm:w-auto sm:ml-auto">
                {/* Entries Selector */}
                <EntriesDropdown
                  value={limit}
                  onChange={(val) => { setLimit(Number(val)); setPage(1); }}
                  options={limitOptions}
                />

                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <AnimatePresence>
                    {(selectedIds.length > 0 || selectAllMatchingSearch) && (
                      <motion.button
                        initial={{ opacity: 0, scale: 0.9, x: 20 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.9, x: 20 }}
                        onClick={handleBulkDelete}
                        disabled={bulkDeleteMutation.isPending}
                        className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 font-bold text-sm"
                      >
                        {bulkDeleteMutation.isPending ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                        <span className="hidden xs:inline">Delete </span>{selectAllMatchingSearch ? (pagination.total || 'All') : selectedIds.length}
                      </motion.button>
                    )}
                  </AnimatePresence>

                  <div className="relative w-40 sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]/60 dark:text-gray-400/60" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-[#D0021B]/5 focus:border-[#D0021B]/20 focus:outline-none transition-all duration-200 text-[#1A1A1A] dark:text-white text-sm placeholder-[#666666]/50"
                    />
                  </div>
                  <button
                    onClick={() => setShowFilterModal(true)}
                    className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white dark:bg-gray-800 border border-[#E5E5E5] dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 text-[#1A1A1A] dark:text-gray-100 text-sm font-medium relative"
                  >
                    <Filter className="w-4 h-4 text-[#666666] dark:text-gray-400" />
                    <span className="hidden sm:inline">Filters</span>
                    {(statusFilter || designationFilter) && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#D0021B] border-2 border-white dark:border-gray-900 rounded-full"></span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      queryClient.invalidateQueries(['cards']);
                      toastUtils.success('Records refreshed');
                    }}
                    className="p-2 text-[#666666] dark:text-gray-400 hover:text-[#D0021B] transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Selection Banner */}
            <AnimatePresence>
              {selectedIds.length > 0 && selectedIds.length === cards.length && pagination.total > cards.length && !selectAllMatchingSearch && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="bg-[#D0021B]/5 border-b border-[#D0021B]/10 overflow-hidden"
                >
                  <div className="px-6 py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-[#D0021B]">
                      <Users className="w-4 h-4" />
                      <span>All <strong>{cards.length}</strong> items on this page are selected.</span>
                    </div>
                    <button
                      onClick={() => setSelectAllMatchingSearch(true)}
                      className="text-sm font-bold text-[#D0021B] hover:underline px-4 py-1"
                    >
                      Select all {pagination.total} matching records
                    </button>
                  </div>
                </motion.div>
              )}
              {selectAllMatchingSearch && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="bg-[#D0021B] overflow-hidden"
                >
                  <div className="px-6 py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-white">
                      <CheckCircle className="w-4 h-4" />
                      <span>All <strong>{pagination.total}</strong> records matching your search are selected.</span>
                    </div>
                    <button
                      onClick={() => {
                        setSelectAllMatchingSearch(false);
                        setSelectedIds([]);
                      }}
                      className="text-sm font-bold text-white hover:underline px-4 py-1 flex items-center gap-1"
                    >
                      <X className="w-4 h-4" /> Clear Selection
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── DESKTOP TABLE (md+) ── */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F9FAFB] dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-800">
                    <th className="w-12 px-6 py-4">
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={cards.length > 0 && (selectAllMatchingSearch || cards.every(c => selectedIds.includes(c._id || c.card_id)))}
                          onChange={toggleSelectAllPage}
                          className="w-4 h-4 text-[#D0021B] border-[#E5E7EB] rounded focus:ring-[#D0021B]/10 cursor-pointer accent-[#D0021B]"
                        />
                      </div>
                    </th>
                    <th className="w-12 px-4 py-4 text-center text-[11px] font-bold text-[#666666] dark:text-gray-400 uppercase tracking-widest">#</th>
                    {renderSortableHeader('Name', 'full_name', 'text-[14px] font-bold uppercase tracking-widest')}
                    {renderSortableHeader('ID Number', 'id_number', 'text-[14px] font-bold uppercase tracking-widest')}
                    {renderSortableHeader('Mobile', 'mobile', 'text-[14px] font-bold uppercase tracking-widest')}
                    {renderSortableHeader('Expiry Date', 'expiry_date', 'text-[14px] font-bold uppercase tracking-widest')}
                    {renderSortableHeader('Status', 'card_status', 'text-[14px] font-bold uppercase tracking-widest')}
                    <th className="px-4 py-4 text-center text-[14px] font-bold text-[#666666] dark:text-gray-400 tracking-widest sticky right-0 bg-[#F9FAFB] dark:bg-[#1E293B] z-10 border-b border-gray-200 dark:border-gray-800">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#D0021B] border-t-transparent"></div>
                        </div>
                      </td>
                    </tr>
                  ) : cards.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-[#666666] dark:text-gray-400">
                        No cards found
                      </td>
                    </tr>
                  ) : (
                    <AnimatePresence>
                      {sortedCards.map((card, index) => {
                        const cardId = card._id || card.card_id;
                        const isSelected = selectedIds.includes(cardId) || selectAllMatchingSearch;
                        return (
                          <motion.tr
                            key={cardId}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className={`border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors duration-200 group/row cursor-default relative z-0 hover:z-10 ${isSelected ? 'bg-[#D0021B]/5 dark:bg-[#D0021B]/10' : ''}`}
                            data-testid={`card-row-${cardId}`}
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleSelectId(cardId)}
                                  className="w-4 h-4 text-[#D0021B] border-gray-300 dark:border-gray-700 rounded focus:ring-[#D0021B]/10 cursor-pointer accent-[#D0021B]"
                                />
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center text-sm font-medium text-[#666666] dark:text-gray-400">
                              {(page - 1) * limit + index + 1}
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-[#E5E5E5] dark:bg-gray-700 overflow-hidden flex-shrink-0">
                                  {card.photo_path || card.has_photo ? (
                                    <img src={`${API_URL}/src/photos/${card.photo_path}`} alt={card.full_name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-[#666666] dark:text-gray-400 font-medium text-sm">
                                      {card.full_name?.charAt(0)?.toUpperCase()}
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-col">
                                  <span className="font-semibold text-[#1A1A1A] dark:text-white">{card.full_name}</span>
                                  <span className="text-xs text-[#666666] dark:text-gray-400 mt-0.5">{card.designation_name || 'No Designation'}</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span className="font-mono text-sm tracking-wider dark:text-gray-300">{card.id_number}</span>
                            </td>
                            <td className="px-4 py-4">
                              {card.mobile ? (
                                <span className="text-sm text-[#1A1A1A] dark:text-gray-300">{card.mobile}</span>
                              ) : (
                                <span className="text-sm text-[#666666] dark:text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              <span className={`text-sm ${getExpiryColor(card.days_until_expiry)}`}>
                                {card.expiry_date ? new Date(card.expiry_date).toLocaleDateString() : '-'}
                              </span>
                            </td>
                            <td className="px-4 py-4">{getStatusBadge(card)}</td>
                            <td className={`px-4 py-4 sticky right-0 border-l border-transparent transition-colors z-[5] ${isSelected ? 'bg-[#fef2f2] dark:bg-[#2d1a1e]' : 'bg-white dark:bg-[#0F172A] group-hover/row:bg-gray-50 dark:group-hover/row:bg-[#1E293B]'}`}>
                              <div className="flex items-center justify-center gap-2">
                                <button onClick={() => setSelectedCard(card._id || card.card_id)} className="p-2 text-[#3B82F6] hover:text-[#1E40AF] hover:bg-blue-50 rounded-lg transition-colors" title="View Card" data-testid={`view-card-${card._id || card.card_id}`}>
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button onClick={() => { setEditingCard(card._id || card.card_id); setShowCardForm(true); }} className="p-2 text-[#10B981] hover:text-[#047857] hover:bg-green-50 rounded-lg transition-colors" title="Edit Card" data-testid={`edit-card-${card._id || card.card_id}`}>
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDelete(card)} className="p-2 text-[#EF4444] hover:text-[#DC2626] hover:bg-red-50 rounded-lg transition-colors" title="Delete Card" data-testid={`delete-card-${card._id || card.card_id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── MOBILE CARD LIST (< md) ── */}
            <div className="md:hidden">
              {isLoading ? (
                <div className="py-12 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#D0021B] border-t-transparent"></div>
                </div>
              ) : cards.length === 0 ? (
                <div className="py-12 text-center text-[#666666] dark:text-gray-400 text-sm">No cards found</div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {sortedCards.map((card, index) => {
                    const cardId = card._id || card.card_id;
                    const isSelected = selectedIds.includes(cardId) || selectAllMatchingSearch;
                    return (
                      <motion.div
                        key={cardId}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.04 }}
                        className={`p-4 ${isSelected ? 'bg-[#D0021B]/5 dark:bg-[#D0021B]/10' : 'bg-white dark:bg-gray-900'}`}
                        data-testid={`card-row-${cardId}`}
                      >
                        {/* Card header: avatar + name + checkbox */}
                        <div className="flex items-center gap-3 mb-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectId(cardId)}
                            className="w-4 h-4 text-[#D0021B] border-gray-300 dark:border-gray-700 rounded accent-[#D0021B] flex-shrink-0"
                          />
                          <div className="w-10 h-10 rounded-full bg-[#E5E5E5] dark:bg-gray-700 overflow-hidden flex-shrink-0">
                            {card.photo_path || card.has_photo ? (
                              <img src={`${API_URL}/src/photos/${card.photo_path}`} alt={card.full_name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[#666666] dark:text-gray-400 font-bold text-sm">
                                {card.full_name?.charAt(0)?.toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-[#1A1A1A] dark:text-white text-sm truncate">{card.full_name}</p>
                            <p className="text-xs text-[#666666] dark:text-gray-400 truncate">{card.designation_name || 'No Designation'}</p>
                          </div>
                          <span className="text-xs font-medium text-[#666666] dark:text-gray-500">#{(page - 1) * limit + index + 1}</span>
                        </div>

                        {/* Label-value rows */}
                        <div className="rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden text-sm">
                          <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50">
                            <span className="font-semibold text-[#666666] dark:text-gray-400">ID Number:</span>
                            <span className="font-mono text-[#1A1A1A] dark:text-gray-200 text-right">{card.id_number}</span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-800">
                            <span className="font-semibold text-[#666666] dark:text-gray-400">Status:</span>
                            <span>{getStatusBadge(card)}</span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
                            <span className="font-semibold text-[#666666] dark:text-gray-400">Mobile:</span>
                            <span className="text-[#1A1A1A] dark:text-gray-200">{card.mobile || '-'}</span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-800">
                            <span className="font-semibold text-[#666666] dark:text-gray-400">Expiry Date:</span>
                            <span className={`font-medium ${getExpiryColor(card.days_until_expiry)}`}>
                              {card.expiry_date ? new Date(card.expiry_date).toLocaleDateString() : '-'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
                            <span className="font-semibold text-[#666666] dark:text-gray-400">Actions:</span>
                            <div className="flex items-center gap-1">
                              <button onClick={() => setSelectedCard(card._id || card.card_id)} className="p-2 text-[#3B82F6] hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="View" data-testid={`view-card-${cardId}`}>
                                <Eye className="w-4 h-4" />
                              </button>
                              <button onClick={() => { setEditingCard(card._id || card.card_id); setShowCardForm(true); }} className="p-2 text-[#10B981] hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Edit" data-testid={`edit-card-${cardId}`}>
                                <Edit className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDelete(card)} className="p-2 text-[#EF4444] hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Delete" data-testid={`delete-card-${cardId}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-[#E5E5E5] dark:border-gray-800 transition-colors gap-3">
              <p className="text-sm text-[#666666] dark:text-gray-400 text-center sm:text-left">
                Showing {cards.length > 0 ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, pagination.total)} of {pagination.total} cards
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="p-2 border border-[#E5E5E5] dark:border-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F5F5F5] dark:hover:bg-gray-800 transition-colors"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="w-4 h-4 text-[#666666] dark:text-gray-400" />
                </button>
                <span className="px-3 sm:px-4 py-2 text-sm text-[#1A1A1A] dark:text-gray-200 whitespace-nowrap">
                  Page {page} of {pagination.total_pages || 1}
                </span>
                <button
                  onClick={() => setPage(Math.min(pagination.total_pages || 1, page + 1))}
                  disabled={page >= (pagination.total_pages || 1)}
                  className="p-2 border border-[#E5E5E5] dark:border-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F5F5F5] dark:hover:bg-gray-800 transition-colors"
                  data-testid="pagination-next"
                >
                  <ChevronRight className="w-4 h-4 text-[#666666] dark:text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </main>

      {/* Modals */}
      <AnimatePresence>
        {selectedCard && (
          <CardPreviewModal
            cardId={selectedCard}
            onClose={() => setSelectedCard(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showImportModal && (
          <ImportModal
            onClose={() => setShowImportModal(false)}
            onSuccess={() => {
              queryClient.invalidateQueries(['cards']);
              queryClient.invalidateQueries(['dashboard-stats']);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showProfileModal && (
          <ProfileModal
            onClose={() => setShowProfileModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCardForm && (
          <CardFormModal
            cardId={editingCard}
            onClose={() => {
              setShowCardForm(false);
              setEditingCard(null);
            }}
            onSuccess={() => {
              queryClient.invalidateQueries(['cards']);
              queryClient.invalidateQueries(['dashboard-stats']);
            }}
          />
        )}
      </AnimatePresence>

      {/* Filter Modal */}
      <AnimatePresence>
        {showFilterModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-2xl shadow-2xl relative"
            >
              <div className="px-6 py-4 border-b border-[#E5E5E5] dark:border-gray-800 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-[#D0021B]" />
                  <h3 className="text-lg font-serif font-bold text-[#1A1A1A] dark:text-white">Filter Options</h3>
                </div>
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-[#666666] dark:text-gray-400" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#666666] dark:text-gray-400 mb-2">Registration Status</label>
                  <CustomDropdown
                    options={statusOptions}
                    value={statusFilter}
                    onChange={(val) => {
                      setStatusFilter(val);
                      setPage(1);
                    }}
                    placeholder="All Statuses"
                    id="modal-status-filter"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#666666] dark:text-gray-400 mb-2">Designation</label>
                  <CustomDropdown
                    options={designationOptions}
                    value={designationFilter}
                    onChange={(val) => {
                      setDesignationFilter(val);
                      setPage(1);
                    }}
                    placeholder="All Designations"
                    id="modal-designation-filter"
                  />
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-[#E5E5E5] dark:border-gray-800 flex items-center justify-between">
                <button
                  onClick={() => {
                    setStatusFilter('');
                    setDesignationFilter('');
                    setLimit(10);
                    setPage(1);
                    toastUtils.success('Filters reset');
                  }}
                  className="text-sm font-medium text-[#666666] dark:text-gray-400 hover:text-[#D0021B] transition-colors"
                >
                  Clear All Filters
                </button>
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="px-6 py-2 bg-[#D0021B] text-white rounded-xl font-medium hover:bg-[#9B0114] shadow-lg shadow-[#D0021B]/20 transition-all active:scale-95"
                >
                  Apply Filters
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}